import React, { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Loader2, Plus, Pencil, Trash2, Tag } from 'lucide-react';
import { toast } from 'sonner';
import { useConfirmation } from '../ConfirmationProvider';

/**
 * Settings → Expense Categories
 *
 * Mirrors `DepartmentsAdmin` in shape. Categories drive the Category dropdown
 * on the Transactions form (debit side only). The `Other` row is system-
 * protected (no delete) so debit transactions always have a fallback.
 */
export const ExpenseCategoriesAdmin = () => {
  const confirm = useConfirmation();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', is_active: true });
  const [saving, setSaving] = useState(false);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/expense-categories?include_inactive=true');
      if (r.data.success) setRows(r.data.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load expense categories');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const openCreate = () => { setEditing(null); setForm({ name: '', description: '', is_active: true }); setDialogOpen(true); };
  const openEdit = (r) => { setEditing(r); setForm({ name: r.name, description: r.description || '', is_active: r.is_active !== false }); setDialogOpen(true); };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/expense-categories/${editing.id}`, form);
        toast.success('Category updated');
      } else {
        await api.post('/expense-categories', form);
        toast.success('Category created');
      }
      setDialogOpen(false);
      fetchRows();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const handleToggleActive = async (r) => {
    try {
      await api.put(`/expense-categories/${r.id}`, { is_active: !r.is_active });
      fetchRows();
    } catch (e) { toast.error(e.response?.data?.detail || 'Toggle failed'); }
  };

  const handleDelete = async (r) => {
    const ok = await confirm({
      title: 'Delete Category',
      description: `Are you sure you want to delete "${r.name}"? Existing debit transactions using this category will block the delete.`
    });
    if (!ok) return;
    try {
      await api.delete(`/expense-categories/${r.id}`);
      toast.success('Category deleted');
      fetchRows();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  const filtered = rows.filter(r => r.name.toLowerCase().includes(filter.toLowerCase()));

  return (
    <Card data-testid="expense-categories-admin-card">
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <div>
          <CardTitle className="text-lg flex items-center gap-2"><Tag className="h-5 w-5 text-amber-500" />Expense Categories</CardTitle>
          <p className="text-sm text-muted-foreground mt-1.5">
            Curate the dropdown shown when adding a debit transaction. The <strong>Other</strong> row is the system fallback and cannot be deleted. Renaming cascades to every transaction that was tagged with this category.
          </p>
        </div>
        <Button onClick={openCreate} size="sm" data-testid="expense-categories-add"><Plus className="h-4 w-4 mr-1.5" />New Category</Button>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between gap-2 mb-3">
          <Input placeholder="Filter…" value={filter} onChange={e => setFilter(e.target.value)} className="max-w-xs h-8" data-testid="expense-categories-filter" />
          <div className="text-xs text-muted-foreground">{filtered.length} of {rows.length}</div>
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
        ) : filtered.length === 0 ? (
          <div className="py-6 text-center text-sm text-muted-foreground">{filter ? 'No matches.' : 'No categories yet.'}</div>
        ) : (
          <div className="border border-border rounded-lg overflow-hidden">
            <table className="w-full text-sm" data-testid="expense-categories-table">
              <thead className="bg-muted/40 text-xs uppercase text-muted-foreground">
                <tr><th className="text-left p-3">Name</th><th className="text-left p-3">Slug</th><th className="text-left p-3">In Use</th><th className="text-left p-3">Active</th><th className="text-right p-3 w-[120px]">Actions</th></tr>
              </thead>
              <tbody>
                {filtered.map(r => (
                  <tr key={r.id} className="border-t border-border hover:bg-muted/20" data-testid={`expense-categories-row-${r.slug}`}>
                    <td className="p-3 font-medium">{r.name} {r.is_system && <Badge variant="outline" className="ml-1.5 text-[10px]">SYSTEM</Badge>}</td>
                    <td className="p-3 font-mono text-xs text-muted-foreground">{r.slug}</td>
                    <td className="p-3 text-muted-foreground">{r.usage_count || 0}</td>
                    <td className="p-3"><Switch checked={r.is_active !== false} onCheckedChange={() => handleToggleActive(r)} /></td>
                    <td className="p-3 text-right">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(r)} data-testid={`expense-categories-edit-${r.slug}`}><Pencil className="h-3.5 w-3.5" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(r)} disabled={r.is_system && r.slug === 'other'} className="text-destructive hover:text-destructive" data-testid={`expense-categories-delete-${r.slug}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="expense-categories-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Category' : 'New Category'}</DialogTitle>
            <DialogDescription>Used by the Category dropdown on debit transactions.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Name *</Label><Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} data-testid="expense-categories-name-input" /></div>
            <div className="space-y-1.5"><Label>Description</Label><Input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="(optional)" data-testid="expense-categories-description-input" /></div>
            <div className="flex items-center justify-between rounded-md border border-border p-3">
              <Label className="text-sm">Active</Label>
              <Switch checked={form.is_active} onCheckedChange={v => setForm(f => ({ ...f, is_active: v }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="expense-categories-save">{saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}{editing ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};
